import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { startRolloutWatch } from '../../api/rollout';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import {
    useEnvironmentWatches,
    useRolloutOverview,
} from '../../hooks/useRollout';
import { ReadinessPanel } from './ReadinessPanel';
import {
    ago,
    buildSummary,
    parseBuilds,
    remaining,
    runningLabel,
    shownWatch,
} from './rolloutModel';
import { WatchDashboard } from './WatchDashboard';
import { WatchHistory } from './WatchHistory';
import './Rollout.scss';

type Tab = 'bewaken' | 'voorbereiden' | 'geschiedenis';

const TABS: { id: Tab; label: string }[] = [
    { id: 'bewaken', label: 'Bewaken' },
    { id: 'voorbereiden', label: 'Voorbereiden' },
    { id: 'geschiedenis', label: 'Geschiedenis' },
];

const useNow = () => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);

        return () => clearInterval(timer);
    }, []);

    return now;
};

/** One environment around its deploys: watch the latest, prepare the next, look back. */
export const RolloutEnvironment = () => {
    const { customerID = '', environment = '' } = useParams();
    const [params, setParams] = useSearchParams();
    const axios = useAxiosSwagger();
    const now = useNow();
    const { rows } = useRolloutOverview();
    const { watches, reload } = useEnvironmentWatches(customerID, environment);
    const [starting, setStarting] = useState(false);

    const row = rows?.find(
        (r) =>
            r.customer.toLowerCase() === customerID.toLowerCase() &&
            r.environment.toLowerCase() === environment.toLowerCase()
    );
    const latest = watches?.[0];
    const running = watches?.find((w) => w.status === 'watching');
    // A watch runs a day after its deploy; the server marks it done within a round after that.
    const runningEnds = running
        ? new Date(
              Date.parse(running.deployedAt) + 24 * 3600 * 1000
          ).toISOString()
        : undefined;
    const pinned = params.get('watch');
    const { watchId, older, unpin } = shownWatch(pinned, latest?.rowKey);
    const tab =
        (params.get('tab') as Tab) ??
        (watches && watches.length === 0 ? 'voorbereiden' : 'bewaken');
    const newProblems = row?.newProblems ?? 0;

    const go = (
        next: Partial<Record<'tab' | 'watch', string>>,
        replace = false
    ) => {
        const merged = new URLSearchParams(params);

        Object.entries(next).forEach(([key, value]) =>
            value ? merged.set(key, value) : merged.delete(key)
        );
        setParams(merged, { replace });
    };

    // The newest watch in the URL (an old link, or opened from the history) would hold the page
    // on it once the next deploy starts a new one; without it in the URL the page follows.
    useEffect(() => {
        if (unpin) go({ watch: undefined }, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unpin]);

    const start = async () => {
        if (!axios) return;

        setStarting(true);

        try {
            await startRolloutWatch(
                axios,
                customerID,
                environment
            );
            await reload();
            // The new watch is the newest, so the page follows it without naming it.
            go({ tab: 'bewaken', watch: undefined });
        } finally {
            setStarting(false);
        }
    };

    return (
        <main className="ro">
            <nav className="ro-crumbs" aria-label="Waar je bent">
                <Link to="/rollout">
                    <FontAwesomeIcon icon="arrow-left" /> Uitrol
                </Link>
                <FontAwesomeIcon
                    icon="angle-right"
                    className="ro-crumbs__sep"
                />
                <strong>
                    {customerID} / {environment}
                </strong>
            </nav>

            <header className="ro-envhead">
                <div>
                    <h2>
                        {customerID} / {environment}
                        <span
                            className={`ro-pill${
                                row?.stage === 'prod' ? ' ro-pill--prod' : ''
                            }`}
                        >
                            {row?.stage === 'prod' ? 'productie' : 'test'}
                        </span>
                    </h2>
                    <div className="ro-envhead__meta">
                        <span
                            title={
                                buildSummary(
                                    parseBuilds(
                                        row?.buildNumbers || row?.builds
                                    )
                                ) || undefined
                            }
                        >
                            Draait nu:{' '}
                            <span className="ro-mono">
                                {runningLabel(row?.buildNumbers, row?.builds)}
                            </span>
                        </span>
                        <span>
                            Laatste deploy: {ago(latest?.deployedAt, now)}
                        </span>
                    </div>
                </div>
                <div className="ro-envhead__actions">
                    {running && runningEnds && Date.parse(runningEnds) > now ? (
                        <span className="ro-pill ro-pill--watching">
                            Wacht loopt · {remaining(runningEnds, now)}
                        </span>
                    ) : (
                        watches && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={start}
                                disabled={starting}
                                title="Begint nu; de 24 uur ervoor zijn de vergelijking"
                            >
                                {starting ? 'Starten…' : 'Wacht starten'}
                            </button>
                        )
                    )}
                </div>
            </header>

            <nav className="ro-tabs" role="tablist" aria-label="Onderdelen">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        className={`ro-tab${tab === t.id ? ' is-active' : ''}`}
                        onClick={() =>
                            go({
                                tab: t.id,
                                watch:
                                    t.id === 'bewaken'
                                        ? pinned ?? undefined
                                        : undefined,
                            })
                        }
                    >
                        {t.label}
                        {t.id === 'bewaken' && newProblems > 0 && (
                            <span className="ro-tab__badge">{newProblems}</span>
                        )}
                    </button>
                ))}
            </nav>

            {tab === 'bewaken' && older && (
                <section className="ro-card ro-card--warn ro-older">
                    <span>Oudere wacht</span>
                    <button
                        type="button"
                        className="ro-more"
                        onClick={() => go({ watch: undefined })}
                    >
                        Naar nieuwste
                    </button>
                </section>
            )}

            {tab === 'bewaken' &&
                (watchId ? (
                    <WatchDashboard watchId={watchId} now={now} />
                ) : (
                    <section className="ro-card">
                        <div className="ro-empty">
                            <strong>Nog geen wacht</strong>
                            Start vanzelf bij een nieuwe build ·{' '}
                            <button
                                type="button"
                                className="ro-more"
                                onClick={() => go({ tab: 'voorbereiden' })}
                            >
                                Voorbereiden
                            </button>
                        </div>
                    </section>
                ))}

            {tab === 'voorbereiden' && (
                <ReadinessPanel
                    customer={customerID}
                    environment={environment}
                    now={now}
                />
            )}

            {tab === 'geschiedenis' && (
                <WatchHistory
                    watches={watches}
                    now={now}
                    onOpen={(id) => go({ tab: 'bewaken', watch: id })}
                />
            )}
        </main>
    );
};
